import * as React from "react";
import {
  Button,
  Combobox,
  Input,
  Label,
  makeStyles,
  Option,
  Tab,
  TabList,
  Text,
  Textarea,
  shorthands,
} from "@fluentui/react-components";
import {
  Add24Regular,
  ArrowRight24Regular,
  CheckmarkCircle24Regular,
  Delete24Regular,
  Dismiss24Regular,
  Search24Regular,
  Send24Regular,
  Sparkle24Regular,
} from "@fluentui/react-icons";
import { DocumentUploadCard } from "./DocumentUploadCard";

interface AppProps {
  title: string;
}

interface Matter {
  id: string;
  visibility?: "private" | "workspace";
  matterNumber: string;
  title?: string | null;
  clientName?: string | null;
}

interface AuthUser {
  id: string;
  email: string;
  displayName?: string | null;
  organizationId: string;
  organizationName?: string | null;
  role: string;
}

interface AuthSession {
  token: string;
  user: AuthUser;
}

interface MatterDocument {
  id: string;
  docType: DocumentKey;
  originalFileName: string;
  blobName: string;
  indexingStatus: string;
}

interface DocumentState {
  files: File[];
  documents: MatterDocument[];
  isUploading: boolean;
  error: string | null;
}

interface ActiveDocumentState {
  text: string;
  isReading: boolean;
  error: string | null;
  lastReadAt: Date | null;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  edits?: ProposedEdit[];
}

interface ProposedEdit {
  operation: "replace";
  targetText: string;
  replacementText: string;
  rationale?: string;
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
}

type ActiveTab = "context" | "assistant";
type DocumentKey = "specification" | "claims" | "figures" | "officeAction" | "citedReferences";

const modelDeployments = [
  { value: "gpt-5.4", label: "GPT 5.4" },
  { value: "gpt-5.4-mini", label: "GPT 5.4 Mini" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
];

const createEmptyDocument = (): DocumentState => ({
  files: [],
  documents: [],
  isUploading: false,
  error: null,
});

const createEmptyDocuments = (): Record<DocumentKey, DocumentState> => ({
  specification: createEmptyDocument(),
  claims: createEmptyDocument(),
  figures: createEmptyDocument(),
  officeAction: createEmptyDocument(),
  citedReferences: createEmptyDocument(),
});

declare const __API_BASE_URL__: string;

const apiBaseUrl = __API_BASE_URL__;
const authStorageKey = "patent-assistant-auth";

const initialChatSession: ChatSession = {
  id: "chat-initial",
  title: "New chat",
  messages: [],
};

const createEmptyActiveDocument = (): ActiveDocumentState => ({
  text: "",
  isReading: false,
  error: null,
  lastReadAt: null,
});

const uploadFields: Array<{
  key: DocumentKey;
  label: string;
  description: string;
  accept?: string;
  required?: boolean;
}> = [
  {
    key: "specification",
    label: "Specification",
    description: "Patent application specification",
  },
  {
    key: "claims",
    label: "Claims",
    description: "Current claim set (required)",
    required: true,
  },
  {
    key: "figures",
    label: "Figures",
    description: "Drawing figures and diagrams",
    accept: ".pdf,.png,.jpg,.jpeg",
  },
  {
    key: "officeAction",
    label: "Office Action",
    description: "USPTO Office Action (required)",
    required: true,
  },
  {
    key: "citedReferences",
    label: "Cited References",
    description: "Prior art references cited",
    accept: ".pdf,.doc,.docx,.txt",
  },
];

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    backgroundColor: "#050505",
    color: "#ffffff",
    boxSizing: "border-box",
    paddingBottom: "20px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "18px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    marginBottom: "18px",
    textAlign: "left",
  },
  title: {
    color: "#f7f8fa",
    lineHeight: "29px",
  },
  subtitle: {
    color: "#a7b0ba",
    lineHeight: "20px",
    maxWidth: "330px",
  },
  accountBar: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    marginBottom: "14px",
  },
  loginPanel: {
    ...shorthands.border("1px", "solid", "#29313a"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("14px"),
    backgroundColor: "#0f1114",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  loginActions: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  tabs: {
    marginBottom: "14px",
  },
  tabPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    color: "#f7f8fa",
    fontWeight: 600,
  },
  input: {
    maxWidth: "100%",
    "& input": {
      backgroundColor: "#101418",
      color: "#f7f8fa",
    },
  },
  matterPanel: {
    ...shorthands.border("1px", "solid", "#29313a"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("12px"),
    backgroundColor: "#0f1114",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  matterHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
  },
  matterTitle: {
    color: "#f7f8fa",
  },
  matterSearchRow: {
    display: "flex",
    gap: "8px",
  },
  matterCombo: {
    flexGrow: 1,
    minWidth: 0,
    "& input": {
      backgroundColor: "#101418",
      color: "#f7f8fa",
    },
  },
  searchInput: {
    flexGrow: 1,
    minWidth: 0,
    "& input": {
      backgroundColor: "#101418",
      color: "#f7f8fa",
    },
  },
  matterMeta: {
    color: "#a9b3be",
  },
  selectedMatter: {
    ...shorthands.border("1px", "solid", "#234033"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("9px"),
    alignItems: "center",
    backgroundColor: "#0d1a14",
    display: "flex",
    gap: "9px",
    justifyContent: "space-between",
  },
  selectedMatterInfo: {
    alignItems: "center",
    display: "flex",
    gap: "9px",
    minWidth: 0,
  },
  selectedMatterActions: {
    display: "flex",
    gap: "4px",
  },
  selectedMatterText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  selectedMatterName: {
    color: "#f7f8fa",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  createForm: {
    ...shorthands.border("1px", "solid", "#29313a"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("10px"),
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  createActions: {
    display: "flex",
    gap: "8px",
  },
  deleteConfirm: {
    ...shorthands.border("1px", "solid", "#5c2d2d"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("9px"),
    backgroundColor: "#1b1111",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  deleteActions: {
    display: "flex",
    gap: "8px",
  },
  grid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    opacity: 1,
  },
  disabledGrid: {
    opacity: 0.45,
    pointerEvents: "none",
  },
  assistantPanel: {
    ...shorthands.border("1px", "solid", "#29313a"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("12px"),
    backgroundColor: "#0f1114",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minHeight: "430px",
  },
  assistantHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  assistantHeaderTop: {
    alignItems: "center",
    display: "flex",
    gap: "10px",
    justifyContent: "space-between",
    width: "100%",
  },
  modelField: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  modelCombo: {
    minWidth: "0",
    width: "100%",
    "& input": {
      backgroundColor: "#101418",
      color: "#f7f8fa",
    },
  },
  chatSessionRow: {
    alignItems: "end",
    display: "grid",
    gap: "8px",
    gridTemplateColumns: "1fr auto",
  },
  chatList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: "10px",
    minHeight: "260px",
  },
  emptyChat: {
    ...shorthands.border("1px", "solid", "#1e252c"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("12px"),
    backgroundColor: "#0b0e11",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  promptList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  message: {
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("10px"),
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    lineHeight: "18px",
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: "#12324a",
    maxWidth: "88%",
  },
  assistantMessage: {
    alignSelf: "flex-start",
    backgroundColor: "#0b0e11",
    maxWidth: "92%",
  },
  proposedEdit: {
    ...shorthands.border("1px", "solid", "#234033"),
    ...shorthands.borderRadius("7px"),
    ...shorthands.padding("9px"),
    backgroundColor: "#0d1a14",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  editCompare: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
  },
  editSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  editTextBlock: {
    ...shorthands.border("1px", "solid", "#1e3a2d"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.padding("8px"),
    backgroundColor: "#08120d",
    boxSizing: "border-box",
    display: "block",
    lineHeight: "17px",
    maxHeight: "96px",
    overflowY: "auto",
    overflowX: "hidden",
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
    width: "100%",
    wordBreak: "break-word",
  },
  chatComposer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  chatTextarea: {
    "& textarea": {
      backgroundColor: "#101418",
      color: "#f7f8fa",
      minHeight: "92px",
    },
  },
  chatActions: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
  },
  contextSummary: {
    color: "#a9b3be",
    lineHeight: "17px",
    minWidth: 0,
  },
  actionArea: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingTop: "2px",
  },
  analyzeButton: {
    ...shorthands.borderRadius("7px"),
    width: "100%",
  },
  trailingIcon: {
    height: "16px",
    marginLeft: "6px",
    width: "16px",
  },
  helpText: {
    color: "#a7b0ba",
    textAlign: "center",
  },
  error: {
    color: "#ff8a8a",
    lineHeight: "17px",
  },
});

function formatMatterLabel(matter: Matter) {
  return [matter.matterNumber, matter.title].filter(Boolean).join(" - ");
}

function createChatSession(): ChatSession {
  return {
    id: `chat-${Date.now()}`,
    title: "New chat",
    messages: [],
  };
}

function formatChatTitle(instruction: string) {
  return instruction.length > 34 ? `${instruction.slice(0, 31)}...` : instruction;
}

function readStoredAuth(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(authStorageKey);
    return raw ? (JSON.parse(raw) as AuthSession) : null;
  } catch (_error) {
    return null;
  }
}

const App: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();
  const [authSession, setAuthSession] = React.useState<AuthSession | null>(() => readStoredAuth());
  const [loginEmail, setLoginEmail] = React.useState("");
  const [loginPassword, setLoginPassword] = React.useState("");
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<ActiveTab>("context");
  const [selectedMatter, setSelectedMatter] = React.useState<Matter | null>(null);
  const [matterSearch, setMatterSearch] = React.useState("");
  const [matterResults, setMatterResults] = React.useState<Matter[]>([]);
  const [selectedMatterOption, setSelectedMatterOption] = React.useState<string[]>([]);
  const [isMatterSearchLoading, setIsMatterSearchLoading] = React.useState(false);
  const [isDeletingMatter, setIsDeletingMatter] = React.useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [createMatterNumber, setCreateMatterNumber] = React.useState("");
  const [createTitle, setCreateTitle] = React.useState("");
  const [createClientName, setCreateClientName] = React.useState("");
  const [createVisibility, setCreateVisibility] = React.useState<"private" | "workspace">("private");
  const [matterError, setMatterError] = React.useState<string | null>(null);
  const [documents, setDocuments] = React.useState<Record<DocumentKey, DocumentState>>(createEmptyDocuments);
  const [activeDocument, setActiveDocument] = React.useState<ActiveDocumentState>(createEmptyActiveDocument);
  const [chatInput, setChatInput] = React.useState("");
  const [chatSessions, setChatSessions] = React.useState<ChatSession[]>([initialChatSession]);
  const [activeChatId, setActiveChatId] = React.useState(initialChatSession.id);
  const [chatError, setChatError] = React.useState<string | null>(null);
  const [isAssistantWorking, setIsAssistantWorking] = React.useState(false);
  const [selectedModelDeployment, setSelectedModelDeployment] = React.useState("gpt-5.4-mini");
  const [appliedEditIds, setAppliedEditIds] = React.useState<Set<string>>(() => new Set());

  const activeChat = chatSessions.find((chat) => chat.id === activeChatId) || chatSessions[0];
  const chatMessages = activeChat?.messages || [];
  const authToken = authSession?.token;

  const clearAuth = React.useCallback(() => {
    window.localStorage.removeItem(authStorageKey);
    setAuthSession(null);
    setSelectedMatter(null);
    setSelectedMatterOption([]);
    setMatterResults([]);
    setMatterSearch("");
    setDocuments(createEmptyDocuments());
  }, []);

  const authenticatedFetch = React.useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers);
      if (authToken) {
        headers.set("Authorization", `Bearer ${authToken}`);
      }

      const response = await fetch(input, {
        ...init,
        headers,
      });

      if (response.status === 401) {
        clearAuth();
      }

      return response;
    },
    [authToken, clearAuth]
  );

  const updateActiveChat = React.useCallback((updater: (chat: ChatSession) => ChatSession) => {
    setChatSessions((prev) => prev.map((chat) => (chat.id === activeChatId ? updater(chat) : chat)));
  }, [activeChatId]);

  const handleNewChat = () => {
    const nextChat = createChatSession();
    setChatSessions((prev) => [nextChat, ...prev]);
    setActiveChatId(nextChat.id);
    setChatError(null);
    setChatInput("");
  };

  const handleLogin = async () => {
    const email = loginEmail.trim();

    if (!email || !loginPassword) {
      setLoginError("Enter your email and password.");
      return;
    }

    setIsLoggingIn(true);
    setLoginError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password: loginPassword,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Sign in failed (${response.status})`);
      }

      const nextSession: AuthSession = {
        token: payload.token,
        user: payload.user,
      };
      window.localStorage.setItem(authStorageKey, JSON.stringify(nextSession));
      setAuthSession(nextSession);
      setLoginPassword("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign in failed";
      setLoginError(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const searchMatters = React.useCallback(async (search: string) => {
    if (!authToken) {
      return;
    }

    setIsMatterSearchLoading(true);
    setMatterError(null);

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/matters?search=${encodeURIComponent(search)}`);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Matter search failed (${response.status})`);
      }

      setMatterResults(payload.matters || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Matter search failed";
      setMatterError(message);
    } finally {
      setIsMatterSearchLoading(false);
    }
  }, [authToken, authenticatedFetch]);

  React.useEffect(() => {
    if (authToken) {
      void searchMatters("");
    }
  }, [authToken, searchMatters]);

  const loadMatter = async (matter: Matter) => {
    setMatterError(null);

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/matters/${matter.id}`);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Unable to load matter (${response.status})`);
      }

      const nextDocuments = createEmptyDocuments();
      (payload.documents || []).forEach((document: MatterDocument) => {
        if (document.docType in nextDocuments) {
          nextDocuments[document.docType].documents.push(document);
        }
      });

      setSelectedMatter(payload.matter);
      setSelectedMatterOption([payload.matter.id]);
      setMatterSearch(formatMatterLabel(payload.matter));
      setIsDeleteConfirmOpen(false);
      setDocuments(nextDocuments);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load matter";
      setMatterError(message);
    }
  };

  const handleCreateMatter = async () => {
    const matterNumber = createMatterNumber.trim();

    if (!matterNumber) {
      setMatterError("Matter number is required.");
      return;
    }

    setMatterError(null);

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/matters`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          matterNumber,
          title: createTitle.trim(),
          clientName: createClientName.trim(),
          visibility: createVisibility,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Matter creation failed (${response.status})`);
      }

      setCreateMatterNumber("");
      setCreateTitle("");
      setCreateClientName("");
      setCreateVisibility("private");
      setIsCreateOpen(false);
      setMatterResults((prev) => [payload.matter, ...prev.filter((matter) => matter.id !== payload.matter.id)]);
      await loadMatter(payload.matter);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Matter creation failed";
      setMatterError(message);
    }
  };

  const handleDeleteMatter = async () => {
    if (!selectedMatter) {
      return;
    }

    setIsDeletingMatter(true);
    setMatterError(null);

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/matters/${selectedMatter.id}`, {
        method: "DELETE",
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Matter deletion failed (${response.status})`);
      }

      setMatterResults((prev) => prev.filter((matter) => matter.id !== selectedMatter.id));
      setSelectedMatter(null);
      setSelectedMatterOption([]);
      setMatterSearch("");
      setIsDeleteConfirmOpen(false);
      setDocuments(createEmptyDocuments());
      await searchMatters("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Matter deletion failed";
      setMatterError(message);
    } finally {
      setIsDeletingMatter(false);
    }
  };

  const uploadFile = async (key: DocumentKey, file: File) => {
    if (!selectedMatter) {
      setDocuments((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          error: "Select or create a matter before uploading.",
        },
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      [key]: { ...prev[key], isUploading: true, error: null },
    }));

    const formData = new FormData();
    formData.append("docType", key);
    formData.append("file", file);

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/matters/${selectedMatter.id}/documents`, {
        method: "POST",
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Upload failed (${response.status})`);
      }

      setDocuments((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          files: prev[key].files.filter((queuedFile) => queuedFile !== file),
          documents:
            key === "citedReferences"
              ? [payload.document, ...prev[key].documents]
              : [payload.document],
          isUploading: false,
          error: null,
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed";
      setDocuments((prev) => ({
        ...prev,
        [key]: { ...prev[key], isUploading: false, error: message },
      }));
    }
  };

  const handleFileChange = (key: DocumentKey, files: File[] | null) => {
    if (!files || files.length === 0) {
      setDocuments((prev) => ({
        ...prev,
        [key]: { ...createEmptyDocument() },
      }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        files: key === "citedReferences" ? [...prev[key].files, ...files] : [files[0]],
        documents: key === "citedReferences" ? prev[key].documents : [],
        error: null,
      },
    }));

    void Promise.all(files.map((file) => uploadFile(key, file)));
  };

  const handleRemoveFile = (key: DocumentKey, index: number) => {
    setDocuments((prev) => {
      const fileNames = [
        ...prev[key].documents.map((document) => document.originalFileName),
        ...prev[key].files.map((file) => file.name),
      ];
      const nameToRemove = fileNames[index];

      return {
        ...prev,
        [key]: {
          ...prev[key],
          files: prev[key].files.filter((file) => file.name !== nameToRemove),
          documents: prev[key].documents.filter((document) => document.originalFileName !== nameToRemove),
          error: fileNames.length <= 1 ? null : prev[key].error,
        },
      };
    });
  };

  const readActiveDocument = async () => {
    setActiveDocument((prev) => ({ ...prev, isReading: true, error: null }));
    setChatError(null);

    try {
      const text = await Word.run(async (context) => {
        const body = context.document.body;
        body.load("text");
        await context.sync();
        return body.text || "";
      });

      setActiveDocument({
        text,
        isReading: false,
        error: null,
        lastReadAt: new Date(),
      });

      return text;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to read the active Word document.";
      setActiveDocument((prev) => ({
        ...prev,
        isReading: false,
        error: message,
      }));
      return null;
    }
  };

  const readWordContext = async () => {
    setActiveDocument((prev) => ({ ...prev, isReading: true, error: null }));
    setChatError(null);

    try {
      const wordContext = await Word.run(async (context) => {
        const body = context.document.body;
        const selection = context.document.getSelection();
        body.load("text");
        selection.load("text");
        await context.sync();
        return {
          documentText: body.text || "",
          selectionText: selection.text || "",
        };
      });

      setActiveDocument({
        text: wordContext.documentText,
        isReading: false,
        error: null,
        lastReadAt: new Date(),
      });

      return wordContext;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to read the active Word document.";
      setActiveDocument((prev) => ({
        ...prev,
        isReading: false,
        error: message,
      }));
      return null;
    }
  };

  const formatEditComment = (edit: ProposedEdit) => {
    const trimForComment = (value: string) => {
      const normalized = value.replace(/\s+/g, " ").trim();
      return normalized.length > 280 ? `${normalized.slice(0, 277)}...` : normalized;
    };

    const summary = edit.rationale?.trim()
      ? trimForComment(edit.rationale)
      : "Applied a targeted revision proposed by Patent Assistant.";

    return `Patent Assistant edit\n\nSummary: ${summary}`;
  };

  const getSearchAnchor = (targetText: string) => {
    const normalized = targetText.replace(/\s+/g, " ").trim();
    return normalized.length > 180 ? normalized.slice(0, 180) : normalized;
  };

  const applyLocatedEdit = async (edit: ProposedEdit, editId: string) => {
    try {
      const applySearchResult = async (
        context: Word.RequestContext,
        options: {
          matchCase: boolean;
        }
      ) => {
        const searchText = getSearchAnchor(edit.targetText);
        const matches = context.document.body.search(searchText, {
          ignorePunct: false,
          ignoreSpace: false,
          matchCase: options.matchCase,
          matchWholeWord: false,
        });
        matches.load("items");
        await context.sync();

        if (matches.items.length === 1) {
          context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
          const targetRange = matches.items[0];
          let replacementRange: Word.Range;

          if (searchText === edit.targetText) {
            replacementRange = targetRange.insertText(edit.replacementText, Word.InsertLocation.replace);
          } else {
            const paragraph = targetRange.paragraphs.getFirst();
            paragraph.load("text");
            await context.sync();

            const targetOccurrenceCount = paragraph.text.split(edit.targetText).length - 1;

            if (targetOccurrenceCount !== 1) {
              return targetOccurrenceCount === 0 ? 0 : targetOccurrenceCount;
            }

            const updatedParagraphText = paragraph.text.replace(edit.targetText, edit.replacementText);
            replacementRange = paragraph.getRange().insertText(updatedParagraphText, Word.InsertLocation.replace);
          }

          replacementRange.insertComment(formatEditComment(edit));
          replacementRange.select();
          await context.sync();
        }

        return matches.items.length;
      };

      const matchCount = await Word.run(async (context) => {
        const exactMatchCount = await applySearchResult(context, { matchCase: true });

        if (exactMatchCount > 0) {
          return exactMatchCount;
        }

        return applySearchResult(context, { matchCase: false });
      });

      if (matchCount === 0) {
        throw new Error("I could not find the target text in the active Word document.");
      }

      if (matchCount > 1) {
        throw new Error("I found the target text more than once, so I did not apply the edit.");
      }

      setAppliedEditIds((prev) => {
        const next = new Set(prev);
        next.add(editId);
        return next;
      });

      updateActiveChat((chat) => ({
        ...chat,
        messages: [
          ...chat.messages,
          {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content: "Applied the located edit with Track Changes enabled and added a comment explaining the change.",
          },
        ],
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to apply the located edit.";
      setChatError(message);
    }
  };

  const handleSendMessage = async () => {
    const instruction = chatInput.trim();

    if (!selectedMatter) {
      setChatError("Select or create a matter before chatting.");
      setActiveTab("context");
      return;
    }

    if (!instruction) {
      setChatError("Enter an instruction for Patent Assistant.");
      return;
    }

    setIsAssistantWorking(true);
    setChatError(null);
    setChatInput("");

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: instruction,
    };
    updateActiveChat((chat) => ({
      ...chat,
      title: chat.title === "New chat" ? formatChatTitle(instruction) : chat.title,
      messages: [...chat.messages, userMessage],
    }));

    const wordContext = await readWordContext();

    if (wordContext === null) {
      setChatError("Open or draft the active Word document, then send your instruction again.");
      setIsAssistantWorking(false);
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/api/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          matterId: selectedMatter.id,
          modelDeployment: selectedModelDeployment,
          instruction,
          activeDocumentText: wordContext.documentText,
          selectedText: wordContext.selectionText,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || `Assistant request failed (${response.status})`);
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: payload.answer || "No response returned.",
        edits: Array.isArray(payload.edits) ? payload.edits : [],
      };

      updateActiveChat((chat) => ({
        ...chat,
        messages: [...chat.messages, assistantMessage],
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Assistant request failed.";
      setChatError(message);
    } finally {
      setIsAssistantWorking(false);
    }
  };

  const matterDocumentCount = Object.values(documents).reduce(
    (total, state) => total + state.documents.length + state.files.length,
    0
  );
  const canChat = Boolean(selectedMatter) && !isAssistantWorking && !activeDocument.isReading;

  if (!authSession) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <Text as="h1" size={600} weight="semibold" className={styles.title}>
            {props.title}
          </Text>
          <Text className={styles.subtitle}>
            Sign in to access your matters and assistant history.
          </Text>
        </div>

        <section className={styles.loginPanel}>
          <div className={styles.field}>
            <Label htmlFor="signin-email" className={styles.label}>
              Email
            </Label>
            <Input
              id="signin-email"
              className={styles.input}
              value={loginEmail}
              autoComplete="email"
              onChange={(_event, data) => setLoginEmail(data.value)}
            />
          </div>
          <div className={styles.field}>
            <Label htmlFor="signin-password" className={styles.label}>
              Password
            </Label>
            <Input
              id="signin-password"
              className={styles.input}
              value={loginPassword}
              type="password"
              autoComplete="current-password"
              onChange={(_event, data) => setLoginPassword(data.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void handleLogin();
                }
              }}
            />
          </div>
          <div className={styles.loginActions}>
            <Button appearance="primary" disabled={isLoggingIn} onClick={handleLogin}>
              {isLoggingIn ? "Signing in..." : "Sign in"}
            </Button>
          </div>
          {loginError ? (
            <Text size={200} className={styles.error}>
              {loginError}
            </Text>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text as="h1" size={600} weight="semibold" className={styles.title}>
          {props.title}
        </Text>
        <Text className={styles.subtitle}>
          Upload relevant documents as context for Patent Assistant.
        </Text>
      </div>

      <div className={styles.accountBar}>
        <Text size={200} className={styles.matterMeta}>
          {authSession.user.displayName || authSession.user.email}
          {authSession.user.organizationName ? ` · ${authSession.user.organizationName}` : ""}
        </Text>
        <Button appearance="subtle" onClick={clearAuth}>
          Sign out
        </Button>
      </div>

      <TabList
        className={styles.tabs}
        selectedValue={activeTab}
        onTabSelect={(_event, data) => setActiveTab(data.value as ActiveTab)}
      >
        <Tab value="context">Context</Tab>
        <Tab value="assistant">Assistant</Tab>
      </TabList>

      <div className={styles.form}>
        {activeTab === "context" ? (
          <div className={styles.tabPanel}>
        <section className={styles.matterPanel}>
          <div className={styles.matterHeader}>
            <Text weight="semibold" className={styles.matterTitle}>
              Matter
            </Text>
            <Button
              appearance="subtle"
              icon={isCreateOpen ? <Dismiss24Regular /> : <Add24Regular />}
              onClick={() => setIsCreateOpen((isOpen) => !isOpen)}
            >
              {isCreateOpen ? "Cancel" : "New"}
            </Button>
          </div>

          {selectedMatter ? (
            <div className={styles.selectedMatter}>
              <div className={styles.selectedMatterInfo}>
                <CheckmarkCircle24Regular />
                <div className={styles.selectedMatterText}>
                  <Text weight="semibold" className={styles.selectedMatterName}>
                    {formatMatterLabel(selectedMatter)}
                  </Text>
                  {selectedMatter.clientName ? (
                    <Text size={200} className={styles.matterMeta}>
                      {selectedMatter.clientName}
                    </Text>
                  ) : null}
                </div>
              </div>
              <div className={styles.selectedMatterActions}>
                <Button
                  appearance="subtle"
                  disabled={isDeletingMatter}
                  icon={<Delete24Regular />}
                  aria-label="Delete selected matter"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                />
                <Button
                  appearance="subtle"
                  disabled={isDeletingMatter}
                  icon={<Dismiss24Regular />}
                  aria-label="Clear selected matter"
                  onClick={() => {
                    setSelectedMatter(null);
                    setSelectedMatterOption([]);
                    setMatterSearch("");
                    setIsDeleteConfirmOpen(false);
                    setDocuments(createEmptyDocuments());
                  }}
                />
              </div>
            </div>
          ) : null}

          {selectedMatter && isDeleteConfirmOpen ? (
            <div className={styles.deleteConfirm}>
              <Text size={200} className={styles.error}>
                Delete this matter and its uploaded documents?
              </Text>
              <div className={styles.deleteActions}>
                <Button
                  appearance="primary"
                  disabled={isDeletingMatter}
                  icon={<Delete24Regular />}
                  onClick={handleDeleteMatter}
                >
                  {isDeletingMatter ? "Deleting..." : "Delete"}
                </Button>
                <Button
                  appearance="secondary"
                  disabled={isDeletingMatter}
                  onClick={() => setIsDeleteConfirmOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {isCreateOpen ? (
            <div className={styles.createForm}>
              <div className={styles.field}>
                <Label htmlFor="new-matter-number" className={styles.label}>
                  Matter Number
                </Label>
                <Input
                  id="new-matter-number"
                  className={styles.input}
                  value={createMatterNumber}
                  placeholder="e.g., 2024-PAT-0847"
                  onChange={(_event, data) => setCreateMatterNumber(data.value)}
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="new-matter-title" className={styles.label}>
                  Title
                </Label>
                <Input
                  id="new-matter-title"
                  className={styles.input}
                  value={createTitle}
                  placeholder="Office Action response"
                  onChange={(_event, data) => setCreateTitle(data.value)}
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="new-matter-client" className={styles.label}>
                  Client
                </Label>
                <Input
                  id="new-matter-client"
                  className={styles.input}
                  value={createClientName}
                  placeholder="Client name"
                  onChange={(_event, data) => setCreateClientName(data.value)}
                />
              </div>
              <div className={styles.field}>
                <Label className={styles.label}>Visibility</Label>
                <Combobox
                  aria-label="Matter visibility"
                  className={styles.modelCombo}
                  value={createVisibility === "workspace" ? "Shared" : "Private"}
                  selectedOptions={[createVisibility]}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue === "private" || data.optionValue === "workspace") {
                      setCreateVisibility(data.optionValue);
                    }
                  }}
                >
                  <Option value="private" text="Private">
                    Private
                  </Option>
                  <Option value="workspace" text="Shared">
                    Shared
                  </Option>
                </Combobox>
              </div>
              <div className={styles.createActions}>
                <Button appearance="primary" icon={<Add24Regular />} onClick={handleCreateMatter}>
                  Create
                </Button>
                <Button appearance="secondary" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          <div className={styles.matterSearchRow}>
            <Combobox
              aria-label="Search matters"
              className={styles.matterCombo}
              value={matterSearch}
              placeholder="Search matter, title, or client"
              selectedOptions={selectedMatterOption}
              onChange={(event) => {
                const value = event.target.value;
                setMatterSearch(value);
                setSelectedMatterOption([]);
                void searchMatters(value);
              }}
              onOptionSelect={(_event, data) => {
                const matter = matterResults.find((result) => result.id === data.optionValue);
                if (matter) {
                  void loadMatter(matter);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void searchMatters(matterSearch);
                }
              }}
            >
              {matterResults.map((matter) => (
                <Option key={matter.id} value={matter.id} text={formatMatterLabel(matter)}>
                  <div className={styles.selectedMatterText}>
                    <Text weight="semibold">{formatMatterLabel(matter)}</Text>
                    {matter.clientName ? (
                      <Text size={200} className={styles.matterMeta}>
                        {matter.clientName}
                      </Text>
                    ) : null}
                  </div>
                </Option>
              ))}
            </Combobox>
            <Button
              appearance="secondary"
              icon={<Search24Regular />}
              onClick={() => void searchMatters(matterSearch)}
            />
          </div>

          {matterError ? (
            <Text size={200} className={styles.error}>
              {matterError}
            </Text>
          ) : null}

          {isMatterSearchLoading ? (
            <Text size={200} className={styles.matterMeta}>
              Loading matters...
            </Text>
          ) : null}
          {!isMatterSearchLoading && matterResults.length === 0 ? (
            <Text size={200} className={styles.matterMeta}>
              No matters found.
            </Text>
          ) : null}
        </section>

        <div className={`${styles.grid} ${selectedMatter ? "" : styles.disabledGrid}`}>
          {uploadFields.map((field) => {
            const fieldState = documents[field.key];
            const fileNames = [
              ...fieldState.documents.map((document) => document.originalFileName),
              ...fieldState.files.map((file) => file.name),
            ];

            return (
              <DocumentUploadCard
                key={field.key}
                label={field.label}
                description={field.description}
                accept={field.accept}
                fileNames={fileNames}
                isUploading={fieldState.isUploading}
                error={fieldState.error}
                multiple={field.key === "citedReferences"}
                onFileChange={(files) => handleFileChange(field.key, files)}
                onRemoveFile={(index) => handleRemoveFile(field.key, index)}
              />
            );
          })}
        </div>

          </div>
        ) : (
          <section className={styles.assistantPanel}>
            <div className={styles.assistantHeader}>
              <div className={styles.assistantHeaderTop}>
                <div>
                  <Text weight="semibold" className={styles.matterTitle}>
                    Assistant
                  </Text>
                  <Text size={200} className={styles.contextSummary}>
                    {selectedMatter
                      ? `${formatMatterLabel(selectedMatter)} · ${matterDocumentCount} context document${
                          matterDocumentCount === 1 ? "" : "s"
                        } · active Word document used on send`
                      : "Select or create a matter in Context first."}
                  </Text>
                </div>
                <Button appearance="secondary" icon={<ArrowRight24Regular />} onClick={() => setActiveTab("context")}>
                  Context
                </Button>
              </div>
              <div className={styles.chatSessionRow}>
                <div className={styles.modelField}>
                  <Label className={styles.label}>Chat</Label>
                  <Combobox
                    aria-label="Chat history"
                    className={styles.modelCombo}
                    value={activeChat?.title || "New chat"}
                    selectedOptions={[activeChatId]}
                    onOptionSelect={(_event, data) => {
                      if (data.optionValue) {
                        setActiveChatId(data.optionValue);
                        setChatError(null);
                      }
                    }}
                  >
                    {chatSessions.map((chat) => (
                      <Option key={chat.id} value={chat.id} text={chat.title}>
                        {chat.title}
                      </Option>
                    ))}
                  </Combobox>
                </div>
                <Button appearance="secondary" icon={<Add24Regular />} onClick={handleNewChat}>
                  New
                </Button>
              </div>
              <div className={styles.modelField}>
                <Label className={styles.label}>Model deployment</Label>
                <Combobox
                  aria-label="Model deployment"
                  className={styles.modelCombo}
                  value={selectedModelDeployment}
                  selectedOptions={[selectedModelDeployment]}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      setSelectedModelDeployment(data.optionValue);
                    }
                  }}
                >
                  {modelDeployments.map((model) => (
                    <Option key={model.value} value={model.value} text={model.label}>
                      {model.label}
                    </Option>
                  ))}
                </Combobox>
              </div>
            </div>

            <div className={styles.chatList}>
              {chatMessages.length === 0 ? (
                <div className={styles.emptyChat}>
                  <Text weight="semibold" className={styles.matterTitle}>
                    What should Patent Assistant do?
                  </Text>
                  <div className={styles.promptList}>
                    <Text size={200} className={styles.matterMeta}>
                      Draft an OA response shell from the uploaded Office Action.
                    </Text>
                    <Text size={200} className={styles.matterMeta}>
                      Review this spec against the inventor disclosure.
                    </Text>
                    <Text size={200} className={styles.matterMeta}>
                      Evaluate my response to the 102 rejection of claim 1.
                    </Text>
                  </div>
                </div>
              ) : null}

              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`${styles.message} ${
                    message.role === "user" ? styles.userMessage : styles.assistantMessage
                  }`}
                >
                  <Text size={200} weight="semibold">
                    {message.role === "user" ? "You" : "Patent Assistant"}
                  </Text>
                  <Text size={200}>{message.content}</Text>
                  {message.edits?.map((edit, index) => {
                    const editId = `${message.id}-edit-${index}`;
                    const isApplied = appliedEditIds.has(editId);

                    return (
                      <div className={styles.proposedEdit} key={editId}>
                        <Text size={200} weight="semibold">
                          Located edit
                        </Text>
                        {edit.rationale ? <Text size={200}>{edit.rationale}</Text> : null}
                        <div className={styles.editCompare}>
                          <div className={styles.editSection}>
                            <Text size={200} weight="semibold">
                              Replace
                            </Text>
                            <div className={styles.editTextBlock}>
                              {edit.targetText}
                            </div>
                          </div>
                          <div className={styles.editSection}>
                            <Text size={200} weight="semibold">
                              With
                            </Text>
                            <div className={styles.editTextBlock}>
                              {edit.replacementText}
                            </div>
                          </div>
                        </div>
                        <Button
                          appearance={isApplied ? "secondary" : "primary"}
                          disabled={isApplied}
                          icon={isApplied ? <CheckmarkCircle24Regular /> : <Sparkle24Regular />}
                          onClick={() => void applyLocatedEdit(edit, editId)}
                        >
                          {isApplied ? "Applied" : "Apply located edit"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className={styles.chatComposer}>
              <Textarea
                className={styles.chatTextarea}
                value={chatInput}
                disabled={!selectedMatter || isAssistantWorking}
                placeholder="Ask Patent Assistant to draft, review, analyze, fill in, or revise the active Word document..."
                onChange={(_event, data) => setChatInput(data.value)}
              />
              <div className={styles.chatActions}>
                <Text size={200} className={styles.matterMeta}>
                  {activeDocument.isReading
                    ? "Reading active Word document..."
                    : "Uses the active Word document when you send."}
                </Text>
                <Button appearance="primary" disabled={!canChat} icon={<Send24Regular />} onClick={handleSendMessage}>
                  {isAssistantWorking ? "Working..." : "Send"}
                </Button>
              </div>
              {chatError || activeDocument.error ? (
                <Text size={200} className={styles.error}>
                  {chatError || activeDocument.error}
                </Text>
              ) : null}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default App;
